// Deterministic census-mapping advisor.
//
// Given the headers AND a sample of the rows from an uploaded census, this
// proposes a column → field mapping for the import review. It is token-free and
// fully local: header-alias knowledge from csv.js plus per-column value shapes
// from columnProfile.js. The user confirms or adjusts the proposal; the census
// loader applies it. Row data is only ever read to classify value shapes –
// nothing here writes participant data anywhere.

import { CSV_COLUMN_INFO } from './constants.js'
import { HEADER_ALIASES, normalizeHeader, isKnownCensusField } from './csv.js'
import { profileColumns, shapeAllowsField, fieldsForShape } from './columnProfile.js'

export const CANONICAL_FIELDS = Object.keys(CSV_COLUMN_INFO)
export const REQUIRED_FIELDS = CANONICAL_FIELDS.filter((field) => CSV_COLUMN_INFO[field].required)

// Split-name pseudo fields. The parser (buildCensusFromRows) already combines
// first_name + last_name into `name`, so the mapper may assign them directly;
// the import review displays them folded into a single Name column.
export const NAME_PART_FIELDS = ['first_name', 'last_name']
export const ASSIGNABLE_FIELDS = [...CANONICAL_FIELDS, ...NAME_PART_FIELDS]

// Fields the review offers in pickers, in census-template order with the name
// parts slotted next to `name`.
export const PICKER_FIELDS = CANONICAL_FIELDS.flatMap((field) =>
  (field === 'name' ? ['name', ...NAME_PART_FIELDS] : [field]))

// Per-field keyword sets used for fuzzy matching: the field-name tokens, every
// known alias spelling that points at the field, plus a few curated synonyms a
// payroll export is likely to use.
const CURATED_KEYWORDS = {
  id: ['employee', 'emp', 'ssn', 'social', 'security', 'number', 'no', 'payroll'],
  name: ['name', 'employee', 'fullname'],
  first_name: ['first', 'given'],
  last_name: ['last', 'surname'],
  dob: ['birth', 'born', 'birthday', 'dob'],
  gender: ['sex', 'gender'],
  hire_date: ['hire', 'hired', 'start', 'started', 'join', 'joined', 'onboard', 'doh'],
  tenure: ['tenure', 'service', 'seniority', 'years'],
  termination_date: ['term', 'terminated', 'separation', 'separated', 'left', 'exit', 'end', 'quit'],
  hours_served: ['hours', 'hrs', 'worked'],
  compensation: ['salary', 'pay', 'wage', 'wages', 'earnings', 'comp', 'income', 'annual'],
  prior_year_compensation: ['prior', 'previous', 'py', 'prev'],
  cb_participant_entry_date: ['cashbalance', 'entry', 'participation', 'commencement'],
  high_3_compensation: ['high', 'highest', 'three', '3', 'average', 'compensation'],
  section_415_compensation: ['415', 'section', 'limitation', 'compensation'],
  pretax_percent: ['deferral', 'deferrals', 'deferred', 'pretax', 'elective', 'contribution', 'percent', 'rate', '401k', '401'],
  pretax_amount: ['deferral', 'deferrals', 'deferred', 'pretax', 'elective', '401k', '401', 'amount', 'dollars'],
  roth_percent: ['roth', 'percent', 'rate'],
  roth_amount: ['roth', 'amount', 'dollars'],
  balance: ['balance', 'account', 'assets', 'vested'],
  loan_balance: ['loan', 'outstanding', 'borrowed'],
  status: ['status', 'active', 'employment'],
  full_time: ['full', 'part', 'time', 'ft', 'pt', 'fte', 'schedule', 'type'],
  key_employee: ['key', 'officer'],
  five_percent_owner: ['owner', 'shareholder', 'principal', 'equity', 'partner'],
  allocation_group: ['class', 'group', 'allocation', 'division', 'div', 'department', 'dept', 'tier', 'category'],
  after_tax_percent: ['aftertax', 'voluntary', 'percent'],
  after_tax_amount: ['aftertax', 'voluntary', 'amount'],
}

// Alias tokens that must NOT leak into a field's fuzzy keywords: generic words
// that would let the field steal matches that belong elsewhere (e.g. the
// "prior_year_salary" alias must not make bare "salary" suggest prior-year
// compensation, and "cash_balance_entry_date" must not claim "Acct Balance").
const ALIAS_TOKEN_BLOCKLIST = {
  cb_participant_entry_date: new Set(['balance']),
  cb_opening_balance: new Set(['balance', 'account', 'acct']),
  prior_year_compensation: new Set(['salary', 'comp']),
  last_name: new Set(['employee', 'name']),
  first_name: new Set(['employee', 'name']),
}

const FIELD_KEYWORDS = (() => {
  const map = Object.fromEntries(ASSIGNABLE_FIELDS.map((field) => [field, new Set(field.split('_'))]))
  for (const [variant, field] of Object.entries(HEADER_ALIASES)) {
    if (!map[field]) continue
    variant.split('_').forEach((token) => {
      if (ALIAS_TOKEN_BLOCKLIST[field]?.has(token)) return
      map[field].add(token)
    })
  }
  for (const [field, words] of Object.entries(CURATED_KEYWORDS)) {
    if (map[field]) words.forEach((word) => map[field].add(word))
  }
  for (const [field, words] of Object.entries(ALIAS_TOKEN_BLOCKLIST)) {
    if (map[field]) words.forEach((word) => map[field].delete(word))
  }
  return map
})()

const tokenize = (text) => String(text).toLowerCase().trim().split(/[^a-z0-9]+/).filter(Boolean)

// Rank fields by token overlap with a header. Exact token hits score most; a
// substring hit ("annualsalary" ⊃ "salary") scores partial, but only for tokens
// of 3+ characters – two-letter tokens like "MI" used to substring-match into
// "ter-MI-nation" and propose middle initials as termination dates.
function rankFields(header, shape) {
  const tokens = tokenize(header)
  if (!tokens.length) return []
  return ASSIGNABLE_FIELDS
    .map((field) => {
      const keywords = FIELD_KEYWORDS[field]
      let score = 0
      for (const token of tokens) {
        if (keywords.has(token)) score += 2
        else if (token.length >= 3 && [...keywords].some((kw) => kw.length >= 4 && (kw.includes(token) || token.includes(kw)))) score += 1
      }
      // Value shapes break header-text ties: a column of $ amounts prefers the
      // amount field over the percent field, dates prefer date fields, etc.
      if (score > 0 && shape && shape !== 'mixed') {
        if (!shapeAllowsField(shape, field)) return { field, score: 0 }
        const preferred = fieldsForShape(shape)
        if (preferred.includes(field)) score += 2
      }
      return { field, score }
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
}

const MIDDLE_INITIAL_RE = /^(mi|m\.?i\.?|middle(_initial)?|initial)$/

const toPickerField = (field) => field

// Propose a mapping for the uploaded columns. `rows` (raw string matrix) is
// optional but strongly recommended: with it, empty columns are skipped, value
// shapes veto contradicted guesses, and unlabeled data columns get shape-based
// suggestions instead of silence. Each mapping entry carries the column's
// profile (shape + samples) for the review UI.
export function proposeColumnMapping(headers, rows = null, columnHints = null) {
  const list = Array.isArray(headers) ? headers : []
  const profiles = Array.isArray(rows) && rows.length ? profileColumns(rows, list.length) : null

  // Pass 1: exact parser knowledge and structural skips.
  const entries = list.map((header, index) => {
    const profile = profiles?.[index] || null
    const shape = profile?.shape || null
    // A label salvaged from a skipped title row, shown only for columns whose
    // own header is a generated "Column N" placeholder – exactly the columns a
    // user otherwise has no way to identify. Display only: a partial banner
    // label ("SH 3%NEC") is a poor matching signal and would mis-map, so the
    // matcher ignores it and lets the user decide from the surfaced text.
    const hint = /^Column \d+$/.test(String(header)) ? String(columnHints?.[index] || '').trim() : ''
    const base = { header, field: null, confidence: 'none', alternatives: [], shape, samples: profile?.samples || [], hint }
    // A column with no values imports nothing – never guess for it, and never
    // let it claim a field a populated column may need.
    if (shape === 'empty') return { ...base, confidence: 'empty' }
    const normalized = normalizeHeader(header)
    if (ASSIGNABLE_FIELDS.includes(normalized) || isKnownCensusField(normalized)) {
      return { ...base, field: toPickerField(normalized), confidence: 'exact' }
    }
    // A 1, 2, 3, … counter column under an unrecognized header is row
    // numbering, not census data (an explicit "Employee ID" 1..N still maps).
    if (profile?.sequentialFromOne) return { ...base, confidence: 'skip' }
    if (MIDDLE_INITIAL_RE.test(normalized)) {
      // Middle initials have no census home; skipping them is a decision, not
      // a failure, so they get their own confidence for the UI.
      return { ...base, confidence: 'skip' }
    }
    return base
  })

  // Pass 2: fuzzy candidates for everything unresolved, then a global
  // best-score-first assignment so the strongest claim wins a contested field
  // (column order used to decide ties, which let spacer columns steal fields).
  const taken = new Set(entries.map((entry) => entry.field).filter(Boolean))
  const candidates = []
  entries.forEach((entry, index) => {
    if (entry.field || entry.confidence === 'empty' || entry.confidence === 'skip') return
    const ranked = rankFields(entry.header, entry.shape)
    entry.alternatives = ranked.slice(0, 4).map((r) => r.field)
    ranked.forEach((r, rankIndex) => candidates.push({ index, field: r.field, score: r.score, rankIndex }))
  })
  candidates.sort((a, b) => b.score - a.score || a.index - b.index || a.rankIndex - b.rankIndex)
  const assignedColumns = new Set()
  for (const candidate of candidates) {
    if (assignedColumns.has(candidate.index) || taken.has(candidate.field)) continue
    const entry = entries[candidate.index]
    entry.field = candidate.field
    entry.confidence = candidate.score >= 4 ? 'likely' : 'low'
    entry.alternatives = entry.alternatives.filter((field) => field !== candidate.field).slice(0, 3)
    assignedColumns.add(candidate.index)
    taken.add(candidate.field)
  }

  // Unlabeled or unrecognized columns that DO hold data: offer shape-based
  // field suggestions (never auto-assign – "Column 22" full of dollar figures
  // could be any money field, and silently guessing is how trust dies).
  for (const entry of entries) {
    if (entry.field || entry.confidence !== 'none' || !entry.shape || entry.shape === 'empty') continue
    entry.alternatives = fieldsForShape(entry.shape).filter((field) => !taken.has(field)).slice(0, 4)
  }

  const mappedFields = new Set(entries.map((entry) => entry.field).filter(Boolean))
  const nameCovered = mappedFields.has('name') || NAME_PART_FIELDS.some((field) => mappedFields.has(field))
  const unmappedRequired = REQUIRED_FIELDS.filter((field) => !mappedFields.has(field))
  return { mapping: entries, unmappedRequired, mappedFields: [...mappedFields], nameCovered }
}

// Whether a parse can skip the import review entirely: every populated column's
// header is one the parser already recognizes on its own. This is the "clean
// template CSV" fast path – anything less goes through the one review surface.
export function headersAllRecognized(headers) {
  const list = Array.isArray(headers) ? headers : []
  if (!list.length) return false
  return list.every((header) => {
    const normalized = normalizeHeader(header)
    return ASSIGNABLE_FIELDS.includes(normalized) || isKnownCensusField(normalized)
  })
}

// Turn a confirmed mapping ([{ header, field }]) into the headerOverrides shape
// parseCsvFile consumes (normalized-header → parser field key). The chosen field
// is itself run through normalizeHeader so every confirmed field uses the
// parser's canonical spelling.
// Entries with no field (left unmapped) are skipped so that column is ignored.
export function buildHeaderOverrides(mapping) {
  const overrides = {}
  for (const entry of mapping || []) {
    if (!entry || !entry.field) continue
    const normalized = String(entry.header).toLowerCase().trim().replace(/\s+/g, '_')
    overrides[normalized] = normalizeHeader(entry.field)
  }
  return overrides
}
